// Isolate and Hide in the 3D view (docs/claude/rendering.md, *Isolate and hide
// in 3D*): what the 3D view keeps out for now — never saved; the plan, the
// layers and the exports untouched. Reset isolate (the Views toolbar) and Show
// all (the status bar, while anything is kept out) bring it back.
import { isolatedParts } from '../isolation';
import {
  renderProperties,
  state,
} from './core';
import { setButtonEnabled } from '../i18n';
import {
  refreshView3d,
  view3d,
  viewer3d,
} from './view3d';
import {
  $,
  deselectAll,
  sectionTargets,
  selectedObjectIds,
  selectionCarry,
} from '../main';

/** Every object the 3D view builds, by id. */
function sceneIds(): string[] {
  const d = state.drawing;
  return [...d.instances, ...(d.walls ?? []), ...(d.polygons ?? []), ...d.connections].map((o) => o.id);
}

/** Show only the selection — and what its rooms carry — in the 3D view. A
 *  room's bench, door or partition (a drawn wall's door…) selected is shown
 *  alone: its room's walls, floor, ceiling and other parts kept out too. */
export function isolateSelection3d(): void {
  const targets = sectionTargets();
  const parts = isolatedParts(targets);
  const keep = parts.size
    ? new Set(targets.map((t) => t.id))
    : new Set([...selectedObjectIds(), ...selectionCarry().map((c) => c.id)]);
  if (!keep.size) return;
  view3d.view3dTempHidden = new Set(sceneIds().filter((id) => !keep.has(id)));
  view3d.view3dIsolatedParts = parts;
  changed();
}

/** Keep the selection out of the 3D view; it is no longer selected. */
export function hideSelection3d(): void {
  const ids = selectedObjectIds();
  if (!ids.size) return;
  for (const id of ids) view3d.view3dTempHidden.add(id);
  deselectAll();
  renderProperties();
  changed();
}

/** Bring back everything Isolate and Hide kept out. */
export function showAll3d(): void {
  if (!anyHidden3d()) return;
  view3d.view3dTempHidden = new Set();
  view3d.view3dIsolatedParts = new Map();
  changed();
}

/** Whether anything is kept out of the 3D view now. */
export const anyHidden3d = (): boolean => view3d.view3dTempHidden.size > 0 || view3d.view3dIsolatedParts.size > 0;

/** Show all is offered while the 3D view shows and something is kept out; the
 *  Views toolbar's Isolate and Reset isolate show with the 3D view, Isolate
 *  live while something is selected, Reset isolate while anything is kept out. */
export function updateShowAll3d(): void {
  $('btn-show-all-3d').classList.toggle('hidden', !(state.viewTab === '3d' && anyHidden3d()));
  const in3d = state.viewTab === '3d';
  for (const id of ['btn-isolate-3d', 'btn-isolate-reset-3d']) $(id).classList.toggle('hidden', !in3d);
  setButtonEnabled($<HTMLButtonElement>('btn-isolate-3d'), in3d && sectionTargets().length > 0, in3d ? 'Select something first' : 'Open the 3D view first');
  setButtonEnabled($<HTMLButtonElement>('btn-isolate-reset-3d'), in3d && anyHidden3d(), in3d ? 'Nothing is isolated' : 'Open the 3D view first');
}

function changed(): void {
  updateShowAll3d();
  viewer3d?.setIsolatedParts(view3d.view3dIsolatedParts);
  refreshView3d(false);
}
